import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import ConsentGate from "@/components/ConsentGate";
import VoiceRecorder from "@/components/VoiceRecorder";
import AstroReportNakshatra from "@/components/AstroReportNakshatra";
import AstroReportPlanetary from "@/components/AstroReportPlanetary";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MailWarning, RotateCcw, Sparkles, Orbit, Coins, Briefcase, Heart } from "lucide-react";

const TABS = [
  { key: "planetary", label: "Planetary", icon: Orbit },
  { key: "money", label: "Money", icon: Coins },
  { key: "career", label: "Career", icon: Briefcase },
  { key: "relationship", label: "Relationship", icon: Heart },
];

export default function AstroScan() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [reports, setReports] = useState(null);
  const [active, setActive] = useState("planetary");

  useEffect(() => {
    const existing = searchParams.get("scan");
    if (!existing) return;
    api.get(`/astro/report/${existing}`)
      .then(({ data }) => { setScanId(existing); setReports(data); setActive("planetary"); })
      .catch(() => {});
  }, [searchParams]);

  const handleAudio = async (blob, filename) => {
    setBusy(true);
    setReports(null);
    const fd = new FormData();
    fd.append("file", blob, filename);
    try {
      const { data } = await api.post("/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const astro = await api.post("/astro/generate", { scan_id: data.scan_id });
      setScanId(data.scan_id);
      setReports(astro.data);
      setActive("planetary");
      toast.success("Your four astro reports are ready.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Recording unusable, please re-record.");
    } finally {
      setBusy(false);
    }
  };

  if (user && !user.email_verified) {
    return (
      <div className="min-h-screen bg-background"><AppHeader />
        <div className="max-w-xl mx-auto px-5 py-20 text-center">
          <MailWarning className="text-primary mx-auto mb-5" size={40} strokeWidth={1.5} />
          <h1 className="font-serif text-3xl mb-3">Verify your email first</h1>
          <p className="text-muted-foreground mb-6">Scanning unlocks once your email is confirmed.</p>
          <Link to="/app"><Button className="rounded-full bg-primary text-primary-foreground font-semibold">Back to dashboard</Button></Link>
        </div>
      </div>
    );
  }
  if (user && !user.consent_accepted) {
    return <div className="min-h-screen bg-background"><AppHeader /><div className="px-5 py-16"><ConsentGate onAccepted={refreshUser} /></div></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {!reports ? (
          <div className="max-w-2xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-3"><Sparkles size={14} strokeWidth={1.5} /> Nakshatra Mitra</span>
            <h1 className="font-serif text-4xl tracking-tight mb-3">One voice, four astro reports</h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">A single 30-second recording reveals your Planetary, Money, Career and Relationship frequencies — as live charts and downloadable PDFs.</p>
            <div className="flex items-center justify-center gap-4 mb-10 flex-wrap">
              {TABS.map((t) => (
                <div key={t.key} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <t.icon size={15} className="text-primary" strokeWidth={1.5} /> {t.label}
                </div>
              ))}
            </div>
            <VoiceRecorder onComplete={handleAudio} busy={busy} />
          </div>
        ) : (
          <div className="space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-1"><Sparkles size={14} strokeWidth={1.5} /> Nakshatra Mitra</span>
                <h1 className="font-serif text-3xl tracking-tight">Your Astro Frequency Reports</h1>
              </div>
              <Button variant="outline" className="rounded-full border-border" onClick={() => setReports(null)} data-testid="astro-rescan-btn"><RotateCcw size={16} strokeWidth={1.5} /> New scan</Button>
            </div>

            <div className="flex gap-2 flex-wrap border-b border-border pb-3">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setActive(t.key)} data-testid={`astro-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${active === t.key ? "bg-primary text-primary-foreground font-semibold" : "border border-border text-muted-foreground hover:border-primary/40"}`}>
                  <t.icon size={15} strokeWidth={1.5} /> {t.label}
                </button>
              ))}
            </div>

            {active === "planetary" && reports.planetary && <AstroReportPlanetary scanId={scanId} report={reports.planetary} grid={reports.grid} />}
            {active === "money" && reports.money && <AstroReportNakshatra scanId={scanId} type="money" report={reports.money} grid={reports.grid} />}
            {active === "career" && reports.career && <AstroReportNakshatra scanId={scanId} type="career" report={reports.career} grid={reports.grid} />}
            {active === "relationship" && reports.relationship && <AstroReportNakshatra scanId={scanId} type="relationship" report={reports.relationship} grid={reports.grid} />}

            <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-6">
              This is a voice-frequency wellness insight for reflection and self-care — not a prediction, medical diagnosis, or professional astrological consultation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
